import { useEffect, useState } from "react";
import { FaArrowRight, FaBars, FaXmark } from "react-icons/fa6";
import { NAV_LINKS } from "@/data/content";
import Logo from "@/components/site/Logo";

const Navbar = () => {
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);

  useEffect(() => {
    let ticking = false;
    const onScroll = () => {
      if (!ticking) {
        requestAnimationFrame(() => {
          setScrolled(window.scrollY > 40);
          ticking = false;
        });
        ticking = true;
      }
    };
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  const go = (href) => {
    setOpen(false);
    if (window.lenis) {
      window.lenis.scrollTo(href, { duration: 1.0 });
    } else {
      document.querySelector(href)?.scrollIntoView({ behavior: "smooth" });
    }
  };

  const scrollToTop = () => {
    if (window.lenis) {
      window.lenis.scrollTo(0, { duration: 1.0 });
    } else {
      window.scrollTo({ top: 0, behavior: "smooth" });
    }
  };

  return (
    <header
      data-testid="navbar"
      className={`hero-fade fixed inset-x-0 top-0 z-[80] transition-all duration-500 ${
        scrolled ? "py-3" : "py-5"
      }`}
      style={{ animationDelay: "1.2s" }}
    >
      <nav
        aria-label="Primary"
        className={`mx-auto flex max-w-[1300px] items-center justify-between px-6 transition-all duration-500 md:px-10 ${
          scrolled ? "bg-white/95 backdrop-blur-md mx-4 rounded-full py-3 shadow-soft md:mx-8 border border-gold-soft/80" : "py-2"
        }`}
      >
        <button
          data-testid="nav-logo"
          onClick={scrollToTop}
          className="flex items-center my-auto transition-opacity hover:opacity-90 focus:outline-none"
          aria-label="PLACE8 home"
        >
          <Logo className="h-8 sm:h-9 md:h-10 lg:h-11 w-auto" />
        </button>

        <div className="hidden items-center gap-7 lg:flex">
          {NAV_LINKS.map((l) => (
            <button
              key={l.href}
              data-testid={`nav-link-${l.label.toLowerCase().replace(/\s/g, "-")}`}
              onClick={() => go(l.href)}
              className="group relative text-[12px] font-bold uppercase tracking-[0.14em] text-green-primary transition-colors hover:text-gold-primary"
            >
              {l.label}
              <span className="absolute -bottom-1 left-0 h-px w-0 bg-gold-primary transition-all duration-300 group-hover:w-full" />
            </button>
          ))}
        </div>

        <div className="flex items-center gap-3">
          <a
            data-testid="nav-hire-talent"
            href="#contact"
            onClick={(e) => {
              e.preventDefault();
              go("#contact");
            }}
            className="btn-gold hidden items-center gap-2 rounded-full px-6 py-3 text-[12px] font-bold uppercase tracking-[0.1em] text-white transition-transform duration-300 hover:-translate-y-0.5 sm:flex"
          >
            Hire Talent
            <FaArrowRight className="text-xs" />
          </a>
          <button
            data-testid="nav-mobile-toggle"
            onClick={() => setOpen((v) => !v)}
            className="grid h-11 w-11 place-items-center rounded-full bg-white border border-gold-soft text-green-primary shadow-sm lg:hidden"
            aria-label="Menu"
          >
            {open ? <FaXmark /> : <FaBars />}
          </button>
        </div>
      </nav>

      {open && (
        <div
          data-testid="mobile-menu"
          className="bg-white mx-4 mt-3 rounded-3xl p-6 shadow-luxe lg:hidden border border-gold-soft"
        >
          <div className="flex items-center justify-between pb-4 mb-2 border-b border-gold-soft/50">
            <Logo className="h-8 w-auto" />
            <span className="text-[10px] font-bold uppercase tracking-widest text-gold-primary">Navigation</span>
          </div>
          <div className="flex flex-col gap-1">
            {NAV_LINKS.map((l) => (
              <button
                key={l.href}
                onClick={() => go(l.href)}
                className="rounded-xl px-4 py-3 text-left text-sm font-bold uppercase tracking-widest text-green-primary hover:bg-gold-light hover:text-gold-primary transition-colors"
              >
                {l.label}
              </button>
            ))}
            <a
              href="#contact"
              onClick={(e) => {
                e.preventDefault();
                go("#contact");
              }}
              className="btn-gold mt-3 flex items-center justify-center gap-2 rounded-full px-6 py-3 text-sm font-bold uppercase tracking-widest text-white"
            >
              Hire Talent <FaArrowRight className="text-xs" />
            </a>
          </div>
        </div>
      )}
    </header>
  );
};

export default Navbar;
